import React, { useEffect, useMemo, useRef, useState } from "react";
import UsersByLocationChart from "../../components/UserByLocationCart.jsx";
import Card from "../../components/card/card.jsx";
import { defaultDashboardData } from "../../data.js";
import { Link, useNavigate } from "react-router-dom";
import LeaveAvailabilityPanel from "../../components/leave/LeaveAvailabilityPanel";
import ViewActionButton from "../../components/table/ViewActionButton";
import axios from "../../api/httpClient";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import { getCurrentMonthBirthdays } from "../../api/birthdays";
import { getCurrentMonthAnniversaries } from "../../api/anniversaries";
import { listUpcomingHolidays } from "../../api/holidays";
import { deleteNotification, listNotifications } from "../../api/notifications";
import { listRequestApplications } from "../../api/requests";
import { getOrganizationSetup } from "../../api/organizationSetup";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import {
  FiUsers,
  FiBarChart2,
  FiBriefcase,
  FiHome,
  FiMapPin,
  FiTrendingUp,
} from "react-icons/fi";

const currentData = defaultDashboardData;
const DASHBOARD_CACHE_KEY = "cehr_dashboard_snapshot_v1";

const DEFAULT_ANALYTICS = {
  employees: 0,
  levels: 0,
  positions: 0,
  departments: 0,
  branches: 0,
};

const formatRelativeTime = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const diffInSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const units = [
    { label: "day", seconds: 86400 },
    { label: "hr", seconds: 3600 },
    { label: "min", seconds: 60 },
  ];
  const unit = units.find((item) => diffInSeconds >= item.seconds);

  if (!unit) {
    return "Just now";
  }

  const valueCount = Math.floor(diffInSeconds / unit.seconds);
  return `${valueCount}${unit.label}${unit.label === "day" && valueCount > 1 ? "s" : ""} ago`;
};

const getNotificationDate = (notification) =>
  notification.deliveredAt ||
  notification.sentAt ||
  notification.createdAt ||
  new Date().toISOString();

const mapDashboardNotification = (notification) => ({
  id: notification.id,
  title: notification.title || "Notification",
  type: notification.type || "general",
  sentAt: notification.sentAt,
  deliveredAt: notification.deliveredAt,
  createdAt: notification.createdAt,
  senderEmail: notification.senderEmail,
  actionUrl: notification.actionUrl,
  actionLabel: notification.actionLabel,
  initials: `${notification.senderEmail || notification.title || "N"}`
    .trim()
    .charAt(0)
    .toUpperCase() || "N",
  message: notification.message || notification.title || "Notification",
  time: formatRelativeTime(getNotificationDate(notification)),
  status:
    notification.status === "Draft"
      ? "pending"
      : notification.status === "Sent"
        ? "approved"
        : "neutral",
});

const mapDashboardRequest = (application) => {
  const applicantName = application.applicantName ||
    [application.employee?.firstName, application.employee?.lastName]
      .filter(Boolean)
      .join(" ") ||
    "Unnamed applicant";
  const requestName = application.requestType?.name || "Request";

  return {
    id: application.id,
    initials:
      applicantName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "RQ",
    image: buildAssetUrl(application.employee?.profilePhotoUrl),
    applicantName,
    requestName,
    message: `${applicantName} submitted ${requestName}.`,
    time: formatRelativeTime(application.submittedAt),
    status: application.status || "Pending",
  };
};

const readDashboardSnapshot = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(DASHBOARD_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      analytics: {
        employees: Number(parsed?.analytics?.employees || 0),
        levels: Number(parsed?.analytics?.levels || 0),
        positions: Number(parsed?.analytics?.positions || 0),
        departments: Number(parsed?.analytics?.departments || 0),
        branches: Number(parsed?.analytics?.branches || 0),
      },
      recentOnboardedStaff: Array.isArray(parsed?.recentOnboardedStaff)
        ? parsed.recentOnboardedStaff
        : [],
      staffByDepartment: Array.isArray(parsed?.staffByDepartment)
        ? parsed.staffByDepartment
        : [],
      birthdayCelebrants: Array.isArray(parsed?.birthdayCelebrants)
        ? parsed.birthdayCelebrants
        : [],
      workAnniversaryCelebrants: Array.isArray(
        parsed?.workAnniversaryCelebrants,
      )
        ? parsed.workAnniversaryCelebrants
        : [],
      upcomingHolidays: Array.isArray(parsed?.upcomingHolidays)
        ? parsed.upcomingHolidays
        : [],
      recentNotifications: Array.isArray(parsed?.recentNotifications)
        ? parsed.recentNotifications
        : [],
      recentRequests: Array.isArray(parsed?.recentRequests)
        ? parsed.recentRequests
        : [],
      birthdayCelebrantsTotal: Number(parsed?.birthdayCelebrantsTotal || 0),
      workAnniversaryCelebrantsTotal: Number(
        parsed?.workAnniversaryCelebrantsTotal || 0,
      ),
    };
  } catch {
    return null;
  }
};

const persistDashboardSnapshot = (snapshot) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      DASHBOARD_CACHE_KEY,
      JSON.stringify(snapshot),
    );
  } catch {
    // Ignore session storage errors and rely on in-memory state.
  }
};

export default function Dashboard() {
  const cachedSnapshot = useMemo(() => readDashboardSnapshot(), []);
  const [analytics, setAnalytics] = useState(
    cachedSnapshot?.analytics || DEFAULT_ANALYTICS,
  );
  const [recentOnboardedStaff, setRecentOnboardedStaff] = useState(
    cachedSnapshot?.recentOnboardedStaff || [],
  );
  const [staffByDepartment, setStaffByDepartment] = useState(
    cachedSnapshot?.staffByDepartment || [],
  );
  const [birthdayCelebrants, setBirthdayCelebrants] = useState(
    cachedSnapshot?.birthdayCelebrants || [],
  );
  const [workAnniversaryCelebrants, setWorkAnniversaryCelebrants] = useState(
    cachedSnapshot?.workAnniversaryCelebrants || [],
  );
  const [upcomingHolidays, setUpcomingHolidays] = useState(
    cachedSnapshot?.upcomingHolidays || [],
  );
  const [recentNotifications, setRecentNotifications] = useState(
    cachedSnapshot?.recentNotifications || [],
  );
  const [recentRequests, setRecentRequests] = useState(
    cachedSnapshot?.recentRequests || [],
  );
  const [setupProgress, setSetupProgress] = useState(null);
  const [birthdayCelebrantsTotal, setBirthdayCelebrantsTotal] = useState(
    cachedSnapshot?.birthdayCelebrantsTotal || 0,
  );
  const [workAnniversaryCelebrantsTotal, setWorkAnniversaryCelebrantsTotal] =
    useState(cachedSnapshot?.workAnniversaryCelebrantsTotal || 0);
  const organizationOverview = useMemo(() => {
    const staffedDepartments = staffByDepartment.filter(
      (department) => Number(department.staffCount || 0) > 0,
    );
    const activeStaff = staffByDepartment.reduce(
      (total, department) => total + Number(department.staffCount || 0),
      0,
    );
    const largestDepartment = staffedDepartments.reduce(
      (largest, department) =>
        !largest || Number(department.staffCount || 0) > Number(largest.staffCount || 0)
          ? department
          : largest,
      null,
    );

    return {
      activeStaff,
      staffedDepartments: staffedDepartments.length,
      averageTeamSize: staffedDepartments.length
        ? Math.round(activeStaff / staffedDepartments.length)
        : 0,
      largestDepartment,
    };
  }, [staffByDepartment]);
  useEffect(() => {
    let active = true;
    getOrganizationSetup().then((progress) => { if (active) setSetupProgress(progress); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const [initialLoading, setInitialLoading] = useState(!cachedSnapshot);
  const [error, setError] = useState("");
  const birthdayCelebrantsRef = useRef(
    cachedSnapshot?.birthdayCelebrants || [],
  );
  const workAnniversaryCelebrantsRef = useRef(
    cachedSnapshot?.workAnniversaryCelebrants || [],
  );
  const birthdayCelebrantsTotalRef = useRef(
    cachedSnapshot?.birthdayCelebrantsTotal || 0,
  );
  const workAnniversaryCelebrantsTotalRef = useRef(
    cachedSnapshot?.workAnniversaryCelebrantsTotal || 0,
  );
  const upcomingHolidaysRef = useRef(cachedSnapshot?.upcomingHolidays || []);
  const recentNotificationsRef = useRef(
    cachedSnapshot?.recentNotifications || [],
  );
  const recentRequestsRef = useRef(cachedSnapshot?.recentRequests || []);

  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const cardData = useMemo(
    () => [
      {
        title: "Employees",
        value: analytics.employees,
        color: "bg-[#E6FDEB] text-[#11980C]",
        icon: <FiUsers className="text-lg sm:text-xl" />,
        to: "/employee",
      },
      {
        title: "Levels",
        value: analytics.levels,
        color: "bg-pink-100 text-pink-800",
        icon: <FiBarChart2 className="text-lg sm:text-xl" />,
        to: "/company-settings/levels",
      },
      {
        title: "Positions",
        value: analytics.positions,
        color: "bg-indigo-100 text-indigo-800",
        icon: <FiBriefcase className="text-lg sm:text-xl" />,
        to: "/company-settings/position",
      },
      {
        title: "Departments",
        value: analytics.departments,
        color: "bg-blue-100 text-blue-800",
        icon: <FiHome className="text-lg sm:text-xl" />,
        to: "/company-settings/departments",
      },
      {
        title: "Branches",
        value: analytics.branches,
        color: "bg-yellow-100 text-yellow-800",
        icon: <FiMapPin className="text-lg sm:text-xl" />,
        to: "/company-settings/branches",
      },
    ],
    [analytics],
  );

  useEffect(() => {
    birthdayCelebrantsRef.current = birthdayCelebrants;
  }, [birthdayCelebrants]);

  useEffect(() => {
    workAnniversaryCelebrantsRef.current = workAnniversaryCelebrants;
  }, [workAnniversaryCelebrants]);

  useEffect(() => {
    birthdayCelebrantsTotalRef.current = birthdayCelebrantsTotal;
  }, [birthdayCelebrantsTotal]);

  useEffect(() => {
    workAnniversaryCelebrantsTotalRef.current = workAnniversaryCelebrantsTotal;
  }, [workAnniversaryCelebrantsTotal]);

  useEffect(() => {
    upcomingHolidaysRef.current = upcomingHolidays;
  }, [upcomingHolidays]);

  useEffect(() => {
    recentNotificationsRef.current = recentNotifications;
  }, [recentNotifications]);

  useEffect(() => {
    recentRequestsRef.current = recentRequests;
  }, [recentRequests]);

  useEffect(() => {
    let active = true;

    const loadDashboardAnalytics = async () => {
      if (!apiBaseUrl) {
        if (active) {
          setError("API base URL is not configured.");
          setInitialLoading(false);
        }
        return;
      }

      if (!cachedSnapshot) {
        setInitialLoading(true);
      }

      try {
        setError("");

        const response = await axios.get(
          `${apiBaseUrl}/organizations/dashboard/analytics`,
        );
        const payload = response?.data || {};
        const nextAnalytics = {
          employees: Number(payload?.analytics?.employees || 0),
          levels: Number(payload?.analytics?.levels || 0),
          positions: Number(payload?.analytics?.positions || 0),
          departments: Number(payload?.analytics?.departments || 0),
          branches: Number(payload?.analytics?.branches || 0),
        };
        const nextRecent = Array.isArray(payload?.recentOnboardedStaff)
          ? payload.recentOnboardedStaff
          : [];
        const nextStaffByDepartment = Array.isArray(payload?.staffByDepartment)
          ? payload.staffByDepartment.map((department) => ({
              id: department.id,
              departmentName:
                department.departmentName || department.name || "Unnamed department",
              staffCount: Number(
                department.staffCount ?? department.totalStaff ?? department.count ?? 0,
              ),
            }))
          : [];

        const resolveImage = (imagePath, name) => buildAssetUrl(imagePath) || createInitialsAvatar(name);

        const nextRecentOnboardedStaff = nextRecent.map((staff) => ({
          id: staff.id,
          image: resolveImage(staff.profilePhotoUrl, staff.name),
          name: staff.name || "Unnamed employee",
          emailAddress: staff.emailAddress || "",
          phoneNumber: staff.phoneNumber || "",
          dateJoined: staff.dateJoined || "",
        }));

        let nextBirthdayCelebrants = birthdayCelebrantsRef.current;
        let nextWorkAnniversaryCelebrants =
          workAnniversaryCelebrantsRef.current;
        let nextBirthdayTotal = birthdayCelebrantsTotalRef.current;
        let nextWorkAnniversaryTotal =
          workAnniversaryCelebrantsTotalRef.current;
        let nextUpcomingHolidays = upcomingHolidaysRef.current;
        let nextRecentNotifications = recentNotificationsRef.current;
        let nextRecentRequests = recentRequestsRef.current;

        try {
          const [
            birthdaysResponse,
            anniversariesResponse,
            upcomingHolidaysResponse,
            notificationsResponse,
            recentRequestsResponse,
          ] = await Promise.all([
            getCurrentMonthBirthdays({ page: 1, limit: 50 }),
            getCurrentMonthAnniversaries({ page: 1, limit: 50 }),
            listUpcomingHolidays({ limit: 3 }),
            listNotifications({ page: 1, limit: 3 }),
            listRequestApplications({ page: 1, limit: 4 }),
          ]);

          const birthdayItems = Array.isArray(birthdaysResponse?.celebrants)
            ? birthdaysResponse.celebrants
            : [];
          const anniversaryItems = Array.isArray(
            anniversariesResponse?.celebrants,
          )
            ? anniversariesResponse.celebrants
            : [];
          const holidayItems = Array.isArray(upcomingHolidaysResponse?.holidays)
            ? upcomingHolidaysResponse.holidays
            : [];

          const toInitials = (firstName, lastName) => {
            const first = `${firstName || ""}`.trim();
            const last = `${lastName || ""}`.trim();
            const initials =
              `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
            return initials || "ST";
          };

          nextBirthdayCelebrants = birthdayItems.map((staff) => ({
            initials: toInitials(staff.firstName, staff.lastName),
            image: resolveImage(staff.profilePhotoUrl),
          }));

          nextWorkAnniversaryCelebrants = anniversaryItems.map((staff) => ({
            initials: toInitials(staff.firstName, staff.lastName),
            image: resolveImage(staff.profilePhotoUrl),
          }));

          nextBirthdayTotal = Number(
            birthdaysResponse?.pagination?.total ?? birthdayItems.length ?? 0,
          );
          nextWorkAnniversaryTotal = Number(
            anniversariesResponse?.pagination?.total ??
              anniversaryItems.length ??
              0,
          );

          const formatHolidayDate = (value) => {
            if (!value) {
              return "-";
            }

            const parsed = new Date(value);

            if (Number.isNaN(parsed.getTime())) {
              return value;
            }

            return parsed.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
            });
          };

          nextUpcomingHolidays = holidayItems.map((holiday) => ({
            id: holiday.id,
            date: formatHolidayDate(holiday.startAt),
            name: holiday.holidayName || "Holiday",
            country: holiday.country || "",
            flag: holiday.flagEmoji || "",
          }));

          nextRecentNotifications = Array.isArray(
            notificationsResponse?.notifications,
          )
            ? notificationsResponse.notifications.map(mapDashboardNotification)
            : [];
          nextRecentRequests = Array.isArray(recentRequestsResponse?.applications)
            ? recentRequestsResponse.applications.map(mapDashboardRequest)
            : [];
        } catch {
          // Keep existing dashboard extras visible when one of these endpoints fails.
        }

        if (!active) {
          return;
        }

        setAnalytics(nextAnalytics);
        setRecentOnboardedStaff(nextRecentOnboardedStaff);
        setStaffByDepartment(nextStaffByDepartment);
        setBirthdayCelebrants(nextBirthdayCelebrants);
        setWorkAnniversaryCelebrants(nextWorkAnniversaryCelebrants);
        setBirthdayCelebrantsTotal(nextBirthdayTotal);
        setWorkAnniversaryCelebrantsTotal(nextWorkAnniversaryTotal);
        setUpcomingHolidays(nextUpcomingHolidays);
        setRecentNotifications(nextRecentNotifications);
        setRecentRequests(nextRecentRequests);

        persistDashboardSnapshot({
          analytics: nextAnalytics,
          recentOnboardedStaff: nextRecentOnboardedStaff,
          staffByDepartment: nextStaffByDepartment,
          birthdayCelebrants: nextBirthdayCelebrants,
          workAnniversaryCelebrants: nextWorkAnniversaryCelebrants,
          upcomingHolidays: nextUpcomingHolidays,
          recentNotifications: nextRecentNotifications,
          recentRequests: nextRecentRequests,
          birthdayCelebrantsTotal: nextBirthdayTotal,
          workAnniversaryCelebrantsTotal: nextWorkAnniversaryTotal,
        });
      } catch (requestError) {
        if (!active) {
          return;
        }

        const message =
          requestError?.response?.data?.message ||
          "Unable to load dashboard analytics.";
        setError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        if (active) {
          setInitialLoading(false);
        }
      }
    };

    loadDashboardAnalytics();

    return () => {
      active = false;
    };
  }, [apiBaseUrl, cachedSnapshot]);

  return (
    <div className="min-h-screen p-3 dashboard-enter sm:p-6">
      <div className=" font-Nunito ">
        <h2 className="text-lg font-bold mb-4">Overview</h2>
        <div className="mb-6 grid grid-cols-3 gap-2 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
          {cardData.map((card, index) => (
            <div
              key={index}
              className={`rounded-xl p-2.5 sm:p-4 ${card.color} cursor-pointer flex flex-col justify-between min-h-[104px] sm:min-h-[112px] group transition-all duration-300 hover:-translate-y-1 hover:shadow-md dashboard-enter dashboard-soft-hover`}
              style={{ animationDelay: `${index * 90}ms` }}
            >
              {card.to ? (
                <Link
                  to={card.to}
                  className="flex h-full flex-col justify-between"
                >
                  <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[10px] font-semibold leading-tight transform transition-transform duration-300 group-hover:translate-x-1 cursor-pointer sm:text-sm sm:font-medium">
                      {card.title}
                    </div>
                    <div className="inline-flex rounded-lg bg-white/70 p-1.5 shadow-sm sm:rounded-xl sm:p-2.5">
                      {card.icon}
                    </div>
                  </div>
                  <div className="mt-2 flex min-h-10 items-end justify-end">
                    {initialLoading ? (
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-current border-t-transparent animate-spin"
                        aria-label={`Loading ${card.title}`}
                        role="status"
                      />
                    ) : (
                      <span className="text-xl font-bold leading-none sm:text-2xl">
                        {card.value}
                      </span>
                    )}
                  </div>
                </Link>
              ) : (
                <div className="flex h-full flex-col justify-between">
                  <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[10px] font-semibold leading-tight transform transition-transform duration-300 group-hover:translate-x-1 sm:text-sm sm:font-medium">
                      {card.title}
                    </div>
                    <div className="inline-flex rounded-lg bg-white/70 p-1.5 shadow-sm sm:rounded-xl sm:p-2.5">
                      {card.icon}
                    </div>
                  </div>
                  <div className="mt-2 flex min-h-10 items-end justify-end">
                    {initialLoading ? (
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-current border-t-transparent animate-spin"
                        aria-label={`Loading ${card.title}`}
                        role="status"
                      />
                    ) : (
                      <span className="text-xl font-bold leading-none sm:text-2xl">
                        {card.value}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {setupProgress && !setupProgress.completed ? <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50 to-rose-50 p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6"><div><p className="text-xs font-bold uppercase tracking-[0.16em] text-orange-700">Set up your workspace</p><h3 className="mt-1 text-xl font-bold text-rose-900">Finish setting up your organisation</h3><p className="mt-1 text-sm text-rose-800">Your progress is saved. Continue from the next incomplete setup step anytime.</p></div><Link to="/organization-setup" className="inline-flex shrink-0 items-center justify-center rounded-xl bg-orange-600 px-5 py-3 text-sm font-bold text-white hover:bg-orange-700">Resume setup</Link></div> : null}
      </div>

      <LeaveAvailabilityPanel />

      <div className="mb-6 grid grid-cols-1 items-stretch gap-5 py-4 dashboard-enter dashboard-enter-delay-1 md:py-5 lg:grid-cols-2 lg:gap-6 lg:py-6">
        <section className="relative flex h-full min-h-[410px] flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-[#071C50] via-[#1234CD] to-[#4F46E5] p-5 text-white shadow-[0_12px_40px_rgba(15,23,42,0.12)] dashboard-soft-hover sm:p-6">
          <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-violet-300/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-cyan-100"><FiTrendingUp className="h-5 w-5" /></span>
              <div><h2 className="text-base font-bold tracking-tight sm:text-lg">Organization overview</h2><p className="mt-0.5 text-xs text-blue-100">Live workforce structure and capacity</p></div>
            </div>
            <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-100">Live data</span>
          </div>
          <div className="relative mt-7 grid grid-cols-2 gap-3 sm:gap-4">
            {[
              { label: "Active staff", value: organizationOverview.activeStaff, icon: <FiUsers /> },
              { label: "Staffed teams", value: organizationOverview.staffedDepartments, icon: <FiHome /> },
              { label: "Average team", value: organizationOverview.averageTeamSize, icon: <FiBarChart2 /> },
              { label: "Open positions", value: analytics.positions, icon: <FiBriefcase /> },
            ].map((metric) => (
              <div key={metric.label} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm sm:p-5">
                <div className="flex items-center justify-between gap-2 text-blue-100"><p className="truncate text-xs font-medium">{metric.label}</p><span className="shrink-0 text-base">{metric.icon}</span></div>
                <p className="mt-3 text-2xl font-extrabold leading-none sm:text-3xl">{initialLoading ? "—" : Number(metric.value || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
          <div className="relative mt-auto flex items-center justify-between gap-4 border-t border-white/15 pt-5">
            <div className="min-w-0"><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-blue-200">Largest team</p><p className="mt-1 truncate text-sm font-semibold sm:text-base">{organizationOverview.largestDepartment?.departmentName || "No staffed department yet"}</p></div>
            {organizationOverview.largestDepartment ? <span className="shrink-0 rounded-xl bg-white px-3 py-2 text-sm font-bold text-[#1234CD]">{Number(organizationOverview.largestDepartment.staffCount || 0).toLocaleString()} staff</span> : null}
          </div>
        </section>

        <div className="h-full min-w-0 dashboard-soft-hover">
          <div className="h-full w-full">
            <UsersByLocationChart
              data={staffByDepartment}
              isLoading={initialLoading}
            />
          </div>
        </div>
      </div>

      <div className="dashboard-enter dashboard-enter-delay-2 dashboard-soft-hover">
        <Card
          {...currentData}
          birthdayCelebrants={birthdayCelebrants}
          workAnniversaries={workAnniversaryCelebrants}
          holidays={upcomingHolidays}
          notifications={recentNotifications}
          onNotificationDeleted={async (notification) => {
            await deleteNotification(notification.id);
            setRecentNotifications((items) => items.filter((item) => item.id !== notification.id));
          }}
          recentRequests={recentRequests}
          birthdayCelebrantsTotal={birthdayCelebrantsTotal}
          workAnniversariesTotal={workAnniversaryCelebrantsTotal}
        />
      </div>

      <div className="w-full min-w-0 max-w-full font-Nunito dashboard-enter dashboard-enter-delay-3">
        <div className="p-3">
          <h3 className="text-base font-bold text-[black] mb-3">
            Last 7 Onboarded Staff
          </h3>

          {initialLoading ? (
            <FetchLoadingState message="Loading dashboard analytics..." className="min-h-[50vh]" />
          ) : null}

          {error ? (
            <p
              className="text-sm text-red-600 mb-4"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <div className="w-full max-w-full overflow-x-auto overscroll-x-contain rounded-xl border border-slate-100 bg-white">
          <table className="w-full min-w-[760px] text-[#00000066] border-collapse md:min-w-[900px]">
            <thead>
              <tr className="text-left text-sm">
                <th className="font-medium py-2 px-4">Name</th>
                <th className="font-medium py-2 px-4">Email address</th>
                <th className="font-medium py-2 px-4">Phone Number</th>
                <th className="font-medium py-2 px-4">Date Joined</th>
                <th className="font-medium py-2 px-4">Actions</th>
              </tr>
            </thead>

            <tbody className=" bg-white text-base rounded-lg mb-8">
              {recentOnboardedStaff.map((user) => (
                <tr
                  key={user.id}
                  className="text-black shadow-sm text-sm rounded-lg leading-8 items-center transition-colors duration-200 hover:bg-[#F8FAFF]"
                >
                  <td className="py-2 px-4">
                    <div className="flex gap-2 items-center">
                      <img
                        className="w-6 rounded-full h-6 object-cover"
                        src={user.image}
                        alt={user.name || "Employee"}
                      />
                      <span>{user.name}</span>
                    </div>
                  </td>
                  <td className="py-2 px-4">{user.emailAddress}</td>
                  <td className="py-2 px-4">{user.phoneNumber}</td>
                  <td className="py-2 px-4">{user.dateJoined}</td>
                  <td className="py-2 px-4">
                    <ViewActionButton
                      onClick={() =>
                        navigate(
                          `/employee/employeespage?id=${encodeURIComponent(user.id)}`,
                        )
                      }
                      label={`View employee ${user.name || "details"}`}
                    />
                  </td>
                </tr>
              ))}

              {!initialLoading &&
              !error &&
              recentOnboardedStaff.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="py-6 px-4 text-sm text-gray-500 text-center"
                  >
                    No onboarded staff found yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}
